import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {APPROVAL_STATUS, APPROVAL_TYPE, ApprovalStatus} from '../../../../constants';
import {trackEvent} from './../../../../tracking/amplitude/TrackingV2';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import Moment from 'moment';
import Util from '../../../shared/util/util';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import ForecastTooltip from '../../../shared/components/tooltips/ForecastTooltip';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import UpdateApprovalStatusMutation from '../../../../mutations/UpdateApprovalStatusMutation';
import {ConfirmationModalPrimaryButton} from '../../../shared/components/modals/general/ConfirmationModal';

const submitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	lastWorkingDayInWeek,
	usingTimeOffApproval,
}) => {
	trackEvent('Submit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const today = Moment();
	const submitDate = !isWeekView
		? currentViewingDate
		: today.isSame(currentViewingDate, 'week')
		? today
		: lastWorkingDayInWeek;
	showModal({
		type: MODAL_TYPE.SUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: submitDate,
		lockedDate,
		usingTimeOffApproval,
	});
};

export type TimesheetApprovalActions = {
	showSubmit: boolean;
	showResubmit: boolean;
	showUnsubmit: boolean;
};

/**
 * Decide which approval actions the period offers.
 *
 * These are not mutually exclusive under the new statuses. A period reads as SUBMITTED as soon
 * as a single row is, and as DECLINED as soon as a single row is, so gating Submit on the period
 * status alone hides it from someone whose period still holds unsubmitted time — logged after a
 * partial submission, or carrying the legacy unsubmitted status. Whenever anything is still
 * awaiting submission, Submit is offered: getting the rest of the period to an approver is what
 * unblocks invoicing.
 *
 * Priority when several apply, highest first: Resubmit (declined time is what blocks the period),
 * then Submit, then Unsubmit. The highest one present renders at full emphasis and the rest are
 * demoted, so the most useful next step reads as the obvious one.
 *
 * Without the new statuses the choice stays lock-driven, as it has always been.
 *
 * @param periodApprovalStatus  aggregate status of the viewed day or week
 * @param hasSubmittableTimeRegs whether any row in the period is still awaiting submission
 * @param isWorkdaysLocked      whether the submission lock covers the period's working days
 */
export const getTimesheetApprovalActions = ({
	periodApprovalStatus,
	hasSubmittableTimeRegs,
	hasNewStatuses,
	isWorkdaysLocked,
}: {
	periodApprovalStatus: string | null;
	hasSubmittableTimeRegs: boolean;
	hasNewStatuses: boolean;
	isWorkdaysLocked: boolean;
}): TimesheetApprovalActions => {
	if (!hasNewStatuses) {
		// Lock-driven rather than status-driven, as it has always been — but with the same
		// "still something to submit" escape, since a legacy client sees rows written under the
		// new statuses as unsubmitted and needs a way to send them on.
		//
		// No DECLINED case to consider: without the flag the read side projects that status to
		// NOT_SUBMITTED, so it never reaches this branch.
		const showUnsubmit = isWorkdaysLocked;
		return {showSubmit: !showUnsubmit || hasSubmittableTimeRegs, showResubmit: false, showUnsubmit};
	}

	if (periodApprovalStatus === APPROVAL_STATUS.APPROVED || periodApprovalStatus === APPROVAL_STATUS.INVOICED) {
		return {showSubmit: false, showResubmit: false, showUnsubmit: false};
	}

	if (periodApprovalStatus === APPROVAL_STATUS.DECLINED) {
		return {showSubmit: hasSubmittableTimeRegs, showResubmit: true, showUnsubmit: false};
	}

	const showUnsubmit =
		periodApprovalStatus === APPROVAL_STATUS.SUBMITTED ||
		(periodApprovalStatus === APPROVAL_STATUS.NOT_SUBMITTED && isWorkdaysLocked);

	return {showSubmit: !showUnsubmit || hasSubmittableTimeRegs, showResubmit: false, showUnsubmit};
};

/**
 * Compute the date range a Resubmit click should target.
 *
 * For 0h weeks (no project time-regs in the entire week), the PWA row is
 * week-scoped and the backend's 0h-path guard requires a full-week range —
 * so we always operate at week granularity regardless of view mode.
 *
 * For non-0h weeks, retain the existing behavior: Week view = full-week range,
 * Day view = single-day range (partial-day resubmit preserved).
 */
export const getResubmitDateRange = (
	currentViewingDate: Moment.Moment,
	isWeekView: boolean,
	hasProjectTimeRegsInWeek: boolean
): {rangeStart: Moment.Moment; rangeEnd: Moment.Moment} => {
	const useWeekRange = isWeekView || !hasProjectTimeRegsInWeek;
	return {
		rangeStart: useWeekRange ? currentViewingDate.clone().startOf('week') : currentViewingDate.clone(),
		rangeEnd: useWeekRange ? currentViewingDate.clone().endOf('week') : currentViewingDate.clone(),
	};
};

const resubmitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	hasProjectTimeRegsInWeek,
}) => {
	trackEvent('Resubmit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const {rangeStart, rangeEnd} = getResubmitDateRange(currentViewingDate, isWeekView, hasProjectTimeRegsInWeek);
	Util.CommitMutation(UpdateApprovalStatusMutation, {
		personIds: [selectedPerson.id],
		approvalType: APPROVAL_TYPE.RESUBMIT,
		startYear: rangeStart.year(),
		startMonth: rangeStart.month() + 1,
		startDay: rangeStart.date(),
		endYear: rangeEnd.year(),
		endMonth: rangeEnd.month() + 1,
		endDay: rangeEnd.date(),
	});
};

const unsubmitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
}) => {
	trackEvent('Unsubmit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const unsubmitDate = !isWeekView ? currentViewingDate : currentViewingDate.clone().startOf('week');
	showModal({
		type: MODAL_TYPE.UNSUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: unsubmitDate,
		companyLockedDate,
		lockedDate,
	});
};

type TimesheetsApprovalButtonsProps = {
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	nonViewerPersonSelected: boolean;
	usingTimeApproval: boolean;
	usingTimeOffApproval: boolean;
	isWeekView: boolean;
	currentViewingDate: Moment.Moment;
	lockedDate?: Moment.Moment;
	companyLockedDate?: Moment.Moment;
	lastWorkingDayInWeek;
	periodTimeApprovalStatus?: ApprovalStatus;
	hasProjectTimeRegsInWeek: boolean;
	hasSubmittableTimeRegsInPeriod: boolean;
};
export const TimesheetsApprovalButtons = ({
	selectedPerson,
	nonViewerPersonSelected,
	usingTimeApproval,
	usingTimeOffApproval,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
	lastWorkingDayInWeek,
	periodTimeApprovalStatus,
	hasProjectTimeRegsInWeek,
	hasSubmittableTimeRegsInPeriod,
}: TimesheetsApprovalButtonsProps): JSX.Element | null => {
	const {formatMessage} = useIntl();

	const handleConfirmTimeOff = (selectedPersonId: number) => {
		trackEvent('Submit Time Off Modal Save Button', 'Clicked');
		const onSuccess = res => {
			const personRes = res.updateApprovalStatus.persons[0];
			if (personRes) {
				createToast({
					duration: 2500,
					message: formatMessage({id: 'time_approval.submit_time_off_toast'}),
				});
			}
		};

		Util.CommitMutation(
			UpdateApprovalStatusMutation,
			{
				personIds: [selectedPersonId],
				approvalType: APPROVAL_TYPE.SUBMIT_TIME_OFF,
				includeTimeOff: true,
			},
			onSuccess
		);
	};

	const handleCloseTimeOffModal = () => {
		trackEvent('Submit Time Off Modal Cancel Button', 'Clicked');
	};

	const openSubmitTimeOffModal = ({nonViewerPersonSelected, selectedPerson}) => {
		trackEvent('Submit Time Off Bottom', 'Clicked', {
			timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
		});

		showModal({
			type: MODAL_TYPE.CONFIRMATION,
			title: formatMessage({id: 'time_approval.submit_time_off'}),
			description: formatMessage({id: 'time_off_approval.submit_timesheets_info'}),
			autoClosePositive: true,
			autoCloseNegative: true,
			callbackPositive: () => handleConfirmTimeOff(selectedPerson.id),
			callbackNegative: handleCloseTimeOffModal,
			includeDefaultMessage: false,
			confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.SAVE,
		});
	};

	const isCompanyLocked = useMemo(() => {
		if (companyLockedDate) {
			return isWeekView
				? companyLockedDate.isSameOrAfter(currentViewingDate.clone().endOf('week'), 'd')
				: companyLockedDate.isSameOrAfter(currentViewingDate, 'd');
		}
		return false;
	}, [isWeekView, companyLockedDate, currentViewingDate]);

	const isWorkdaysLocked = useMemo(() => {
		if (lockedDate && lastWorkingDayInWeek) {
			return lockedDate.isSameOrAfter(lastWorkingDayInWeek, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, lastWorkingDayInWeek]);

	const messageIdSuffix = (usingTimeOffApproval ? '_and_time_off' : '') + '_info';

	if (!usingTimeApproval && !usingTimeOffApproval) return null;

	const hasNewStatuses = hasFeatureFlag('timesheet_approval_new_statuses');

	const {showSubmit, showResubmit, showUnsubmit} = getTimesheetApprovalActions({
		periodApprovalStatus: periodTimeApprovalStatus ?? null,
		hasSubmittableTimeRegs: hasSubmittableTimeRegsInPeriod,
		hasNewStatuses,
		isWorkdaysLocked,
	});

	return usingTimeApproval ? (
		<>
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.MONO}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
							})
						}
						// Same rule as the timesheet Submit below: time off submits through the
						// same mutation branch, which resolves its locked date without a person,
						// so only a company lock can stop it.
						disabled={isCompanyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
			{showResubmit && (
				<Button
					emphasis={EMPHASIS.HIGH}
					onPress={() =>
						resubmitTimesheets({
							nonViewerPersonSelected,
							selectedPerson,
							isWeekView,
							currentViewingDate,
							hasProjectTimeRegsInWeek,
						})
					}
					data-cy={'resubmit-timesheets-button'}
				>
					{nonViewerPersonSelected
						? formatMessage({id: 'time_approval.resubmit_names_timesheets'}, {name: selectedPerson.firstName})
						: formatMessage({id: 'time_approval.resubmit_timesheets'})}
				</Button>
			)}
			{showSubmit && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_timesheets' + messageIdSuffix,
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_timesheets' + messageIdSuffix,
							  })
					}
				>
					<Button
						emphasis={showResubmit ? EMPHASIS.MEDIUM : EMPHASIS.HIGH}
						onPress={() =>
							submitTimesheets({
								nonViewerPersonSelected,
								selectedPerson,
								isWeekView,
								currentViewingDate,
								lockedDate,
								lastWorkingDayInWeek,
								usingTimeOffApproval,
							})
						}
						// Only a company lock can stop a submit. The owner's own submit lock must not:
						// it covers precisely the period whose outstanding time this button exists
						// to send on, so gating on it renders the button inert exactly when it is
						// needed. The server agrees — SUBMIT resolves its locked date without a
						// person, so it enforces the company lock alone.
						disabled={isCompanyLocked}
						data-cy={'submit-timesheets-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_timesheet',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_timesheet',
							  })}
					</Button>
				</ForecastTooltip>
			)}
			{showUnsubmit && (
				// Lowest of the three priorities, so it steps down whenever Submit is also offered.
				<Button
					emphasis={showSubmit ? EMPHASIS.MEDIUM : EMPHASIS.HIGH}
					onPress={() =>
						unsubmitTimesheets({
							nonViewerPersonSelected,
							selectedPerson,
							isWeekView,
							currentViewingDate,
							lockedDate,
							companyLockedDate,
						})
					}
					disabled={isCompanyLocked}
					data-cy={'unsubmit-timesheets-button'}
				>
					{nonViewerPersonSelected
						? formatMessage(
								{
									id: 'time_approval.unsubmit_names_timesheets',
								},
								{
									name: selectedPerson.firstName,
								}
						  )
						: formatMessage({
								id: 'time_approval.unsubmit_timesheets',
						  })}
				</Button>
			)}
		</>
	) : (
		<>
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
							})
						}
						// Same rule as the timesheet Submit below: time off submits through the
						// same mutation branch, which resolves its locked date without a person,
						// so only a company lock can stop it.
						disabled={isCompanyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
		</>
	);
};
